import { createPortal } from 'react-dom'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface TopBarFilterOption {
    value: string
    label: string
}

export interface TopBarFilterDropdownProps {
    value: string
    options: TopBarFilterOption[]
    onChange: (value: string) => void
    loading?: boolean
    icon?: ReactNode
    label?: string
}

export function TopBarFilterDropdown({
    value,
    options,
    onChange,
    loading,
    icon,
    label,
}: TopBarFilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })
    const selectedOption = options.find((opt) => opt.value === value) ?? options[0]

    useLayoutEffect(() => {
        if (!isOpen || !triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        setPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 180) })
    }, [isOpen])

    return (
        <div className="relative w-full">
            {label && <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</label>}
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((prev) => !prev)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') setIsOpen(false)
                }}
                className={cn(
                    "flex h-8 w-full items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold",
                    "bg-white/50 backdrop-blur-sm transition-colors duration-150 dark:bg-white/10",
                    "text-foreground hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                    isOpen
                        ? "border-primary/50 shadow-sm ring-1 ring-primary/20"
                        : "border-white/40 hover:border-primary/30 hover:bg-white/70 dark:border-white/20 dark:hover:bg-white/15"
                )}
            >
                <div className="flex items-center gap-1.5 truncate">
                    {icon && <span className="text-muted-foreground">{icon}</span>}
                    <span className="truncate">{selectedOption?.label || 'Select'}</span>
                </div>
                {loading ? (
                    <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-primary" />
                ) : (
                    <ChevronDown className={cn("h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform duration-150", isOpen && "rotate-180 text-primary")} />
                )}
            </button>

            {isOpen && createPortal(
                <>
                    <button
                        type="button"
                        aria-label="Close menu"
                        className="fixed inset-0 z-[9998]"
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        role="listbox"
                        className="fixed z-[9999] max-h-64 overflow-y-auto rounded-xl border border-border/50 bg-background/95 py-1 shadow-xl backdrop-blur-2xl"
                        style={{ top: pos.top, left: pos.left, width: pos.width }}
                    >
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                role="option"
                                aria-selected={value === option.value}
                                onClick={() => {
                                    onChange(option.value)
                                    setIsOpen(false)
                                }}
                                className={cn(
                                    "flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors duration-150",
                                    value === option.value
                                        ? "bg-primary/10 font-bold text-primary"
                                        : "font-medium text-foreground hover:bg-accent/50"
                                )}
                            >
                                {option.label}
                                {value === option.value && <Check className="h-3.5 w-3.5" />}
                            </button>
                        ))}
                    </div>
                </>,
                document.body
            )}
        </div>
    )
}
